import * as React from 'react';

import type { W_BillingCode_FindForJob_VM_Single } from 'ab-api/web/billingCode/findForJob';

import type WorkSummaryVM from 'ab-viewModels/fieldReport/workSummary.viewModel';
import type WorkSummaryDetailsVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';
import type SiblingSubjobVM from 'ab-viewModels/workRequest/siblingSubjob.viewModel';

import { bemElement } from 'af-utils/bem.util';
import { downloadCSV } from 'af-utils/csv.utils';
import { dollarFormatter } from 'af-utils/format.util';

import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import { Button } from '@acceligentllc/storybook';

import BillingTypeIndicator from '../../BillingTypeIndicator';
import { toCSVData } from '../../helpers';
import BillableAndNonBillableWorkTable from '../Table/BillableAndNonBillableWorkTable';

interface OwnProps {
	workSummaryDetails: WorkSummaryDetailsVM[];
	workSummary: WorkSummaryVM[];
	billingCodes: Record<string, W_BillingCode_FindForJob_VM_Single[]>;
	onRowHighlight: (groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => void;
	highlightedGroup: Nullable<string>;
	highlightedGroupKey: Nullable<string>;
	workSummaryDetailsByGroupKey: Record<string, WorkSummaryDetailsVM[]>;
	areFRsReadOnly: boolean;
	isWorkSummaryStatusCompleted: boolean;
	isWorkSummaryStatusReviewed: boolean;
	alternativeWorkSummary: Set<WorkSummaryVM>;
	workOrderCode: string;
	belongsToProject: boolean;
	subJobs: SiblingSubjobVM[];
	onReallocateAll: (billingType: WorkSummaryBillingType, subJob: SiblingSubjobVM) => void;
	onBulkEditDetailBillableStatus: (workSummaryDetailIds: number[], isBillable: boolean) => void;
	handleJobReallocation: (subJob: SiblingSubjobVM, workSummaryDetailIds: number[]) => Promise<void>;
	fieldReportId: number;
	workOrderId: number;
}

type Props = OwnProps;

const _reduceTotalMaterialCost = (_acc: number, _curr: WorkSummaryVM) => {
	return _acc + (_curr.materialCost ?? 0);
};

const WorkSummaryMaterialCost: React.FC<Props> = (props) => {
	const {
		workSummaryDetails,
		workSummary,
		billingCodes,
		onRowHighlight,
		highlightedGroup,
		areFRsReadOnly,
		isWorkSummaryStatusCompleted,
		isWorkSummaryStatusReviewed,
		highlightedGroupKey,
		workSummaryDetailsByGroupKey,
		alternativeWorkSummary,
		belongsToProject,
		subJobs,
		handleJobReallocation,
		onReallocateAll,
		fieldReportId,
		workOrderId,
		onBulkEditDetailBillableStatus,
		workOrderCode,
	} = props;

	const handleBulkEditDetailBillableStatus = React.useCallback((workSummaryDetailIds: number[]) => {
		onBulkEditDetailBillableStatus(workSummaryDetailIds, false);
	}, [onBulkEditDetailBillableStatus]);

	const onExportClick = React.useCallback(() => {
		const csvData = toCSVData(workSummary, belongsToProject, WorkSummaryBillingType.MATERIAL_COST);
		downloadCSV(csvData, `${workOrderCode}_material_expense.csv`);
	}, [belongsToProject, workOrderCode, workSummary]);

	const totalBillableWork = workSummary.reduce(_reduceTotalMaterialCost, 0);
	const showHint = totalBillableWork === 0;

	const billableWorkClassName = 'field-report__work-summary__billable-work';
	const headerClassName = bemElement(billableWorkClassName, 'header');

	const isWorkSummaryReadonly = isWorkSummaryStatusCompleted || areFRsReadOnly || isWorkSummaryStatusReviewed;
	const isBillableStatusEditable = !(isWorkSummaryStatusCompleted || areFRsReadOnly);

	return (
		<>
			<div className={billableWorkClassName}>
				<div className={headerClassName}>
					<BillingTypeIndicator billingType={WorkSummaryBillingType.MATERIAL_COST} />
					<span className={bemElement(headerClassName, 'title')}>
						Material Expenses
					</span>
					<Button
						disabled={!workSummary.length}
						icon="ActionDownload"
						label="Export"
						onClick={onExportClick}
						style="link"
					/>
				</div>
				<BillableAndNonBillableWorkTable
					alternativeWorkSummary={alternativeWorkSummary}
					belongsToProject={belongsToProject}
					billingCodes={billingCodes}
					billingType={WorkSummaryBillingType.MATERIAL_COST}
					fieldReportId={fieldReportId}
					handleJobReallocation={handleJobReallocation}
					highlightedGroup={highlightedGroup}
					highlightedGroupKey={highlightedGroupKey}
					isBillable={true}
					isBillableStatusEditable={isBillableStatusEditable}
					isReadOnly={isWorkSummaryReadonly}
					onBulkEditDetailBillableStatus={handleBulkEditDetailBillableStatus}
					onReallocateAll={onReallocateAll}
					onRowHighlight={onRowHighlight}
					subJobs={subJobs}
					workOrderId={workOrderId}
					workSummary={workSummary}
					workSummaryDetails={workSummaryDetails}
					workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
				/>
				<div className={bemElement('field-report__work-summary__billable-work', 'total')}>
					{showHint && (
						<span className={bemElement('field-report__work-summary__billable-work__total', 'hint')}>
							Please add a material cost to at least one row to calculate the total
						</span>
					)}
					{`Total ${dollarFormatter.format(totalBillableWork)}`}
				</div>
			</div>
		</>
	);
};

export default React.memo(WorkSummaryMaterialCost);
